import {Role} from "../api/Role";
import {injectable} from "inversify";
import {GroupPermission} from "../../generated/protocol";
import {FeatureToggleService} from "./FeatureToggleService";

export const MENU_SERVICE_NAME = "menuService";

export class PermissionCategory {
    static METRICS = "";
    static USERS = "USERS";
    static TEST = "TEST";
    static DASHBOARD_USER_GROUPS = "DASHBOARD_USER_GROUPS";
    static ACHIEVEMENTS = "ACHIEVEMENTS";
    static WALLET = "WALLET";
    static POKER_TOURNAMENTS = "POKER_TOURNAMENTS";
    static ADMIN_DASHBOARD_USERS = "ADMIN_DASHBOARD_USERS";
    static BONUS = "BONUS";
    static OPERATOR_PROGRESSION = "OPERATOR_PROGRESSION";
    static ITEMS = "ITEMS";
    static OPERATOR = "OPERATOR";
    static EVENTS = "EVENTS";
    static DASHBOARD_AUTHENTICATION = "DASHBOARD_AUTHENTICATION";
    static HAND_HISTORY = "HAND_HISTORY";
    static SPIN_HISTORY = "SPIN_HISTORY";
    static ANALYZER = "ANALYZER";
    static PRODUCTS = "PRODUCTS";
}

export interface MenuService {
    getMenuRoutes(role: string, permissions: { [key: string]: GroupPermission }): Array<any>;

    isDisplayInMenu(route: any): boolean;

    getRoutes(role: string, permissions: { [key: string]: GroupPermission }): Array<any>;

    contains(rolesAllowed: Array<string>, role: string): boolean;

    hasPermission(categories: string[], requiresWriteAccess: boolean, permissions: { [key: string]: GroupPermission }): boolean;
}

export interface MenuRoute {
    path: string,
    label: string,
    icon?: string;
    displayInMenu?: boolean;
    rolesAllowed: Role[];
    children?: MenuRoute[];
}

@injectable()
export class MenuServiceImpl implements MenuService {
    routes: Array<any> = [];

    constructor() {
        this.routes = [
            {
                path: '/user-groups',
                icon: "user",
                label: "Dashboard User Groups",
                rolesAllowed: [Role.ADMIN, Role.OPERATOR],
                permissions: [PermissionCategory.ADMIN_DASHBOARD_USERS],
            },

            {
                path: '/tournaments', icon: "calendar", label: "Tournaments", rolesAllowed: [Role.ADMIN, Role.OPERATOR],
                permissions: [PermissionCategory.POKER_TOURNAMENTS],
                children: [
                    {
                        path: '/tournaments',
                        displayInMenu: true,
                        label: "Scheduled Tournament",
                        rolesAllowed: [Role.ADMIN, Role.OPERATOR]
                    },
                    {
                        path: '/sit-and-gos',
                        displayInMenu: true,
                        label: "Sit & Go's",
                        rolesAllowed: [Role.ADMIN, Role.OPERATOR]
                    },
                    {
                        path: '/tournament/:id',
                        displayInMenu: false,
                        label: "Create Tournament",
                        rolesAllowed: [Role.ADMIN, Role.OPERATOR]
                    },
                    {
                        path: '/tournament-structures/blinds',
                        displayInMenu: true,
                        label: "Blinds Structures",
                        rolesAllowed: [Role.ADMIN],
                        writeAccess: false
                    },
                    //{ path : '/tournament-structures/payouts', displayInMenu : true,  label : "Payout Structures", rolesAllowed : [Role.ADMIN], writeAccess : false},
                    {
                        path: '/sit-and-go/:id',
                        displayInMenu: false,
                        label: "Create Sit & Go",
                        rolesAllowed: [Role.ADMIN, Role.OPERATOR],
                        writeAccess: true
                    },
                ]
            },
            {
                path: '/bonuses', icon: "star-o", label: "Bonus", rolesAllowed: [Role.ADMIN, Role.OPERATOR],
                permissions: [PermissionCategory.BONUS]
            },

            {
                path: '/triggers', icon: "bolt", label: "Triggers", rolesAllowed: [Role.ADMIN, Role.OPERATOR],
                permissions: [PermissionCategory.ACHIEVEMENTS],
                children: [
                    {
                        path: '/trigger/new',
                        displayInMenu: true,
                        label: "Create Trigger",
                        rolesAllowed: [Role.ADMIN, Role.OPERATOR],
                        writeAccess: true
                    },
                    {
                        path: '/trigger/:id',
                        displayInMenu: false,
                        label: "Create Trigger",
                        rolesAllowed: [Role.ADMIN, Role.OPERATOR]
                    },
                    {
                        path: '/trigger/activation',
                        displayInMenu: true,
                        label: "Active Triggers",
                        rolesAllowed: [Role.ADMIN, Role.OPERATOR],
                        writeAccess: true
                    }
                ]
            },
            {
                path: '/users', icon: "user", label: "Users", rolesAllowed: [Role.ADMIN, Role.OPERATOR],
                permissions: [PermissionCategory.USERS]
            },
            {
                path: '/mass-update-accounts', icon: "exchange", label: "Mass Update", rolesAllowed: [Role.ADMIN, Role.OPERATOR],
                permissions: [PermissionCategory.WALLET],
            },
            {
                path: '/item', icon: "gift", label: "Items", rolesAllowed: [Role.ADMIN, Role.OPERATOR],
                permissions: [PermissionCategory.ITEMS],
                children: [
                    {
                        path: '/item/new',
                        displayInMenu: true,
                        label: "New Item",
                        rolesAllowed: [Role.ADMIN, Role.OPERATOR],
                        writeAccess: true
                    }
                ]
            },

            {
                path: '/product', icon: "gift", label: "Products", rolesAllowed: [Role.ADMIN, Role.OPERATOR],
                permissions: [PermissionCategory.PRODUCTS],
                children: [
                    {
                        path: '/product/new',
                        displayInMenu: true,
                        label: "Create Product",
                        rolesAllowed: [Role.ADMIN, Role.OPERATOR],
                        writeAccess: true
                    },
                    {
                        path: '/product/:id',
                        displayInMenu: false,
                        label: "Create Product",
                        rolesAllowed: [Role.ADMIN, Role.OPERATOR]
                    }
                ]
            },
            {
                path: '/hand-history',
                icon: "history",
                label: "Hand History",
                rolesAllowed: [Role.ADMIN, Role.OPERATOR],
                permissions: [PermissionCategory.HAND_HISTORY]
            },
            {
                path: 'blackjack/hand-history',
                icon: "history",
                label: "Blackjack Hand History",
                rolesAllowed: [Role.ADMIN, Role.OPERATOR],
                permissions: [PermissionCategory.HAND_HISTORY]
            },
            {
                path: 'slots/admin-spin-history',
                displayInMenu: FeatureToggleService.spinHistoryFeatureFlag.isEnabled,
                icon: "history",
                label: "Slots Spin History",
                rolesAllowed: [Role.ADMIN, Role.OPERATOR],
                permissions: [PermissionCategory.SPIN_HISTORY]
            },
            {
                path: 'caribbean-poker/hand-history',
                icon: "history",
                label: "Caribbean Poker Hand History",
                rolesAllowed: [Role.ADMIN, Role.OPERATOR],
                permissions: [PermissionCategory.HAND_HISTORY]
            },
            {
                path: 'casino-holdem/hand-history',
                icon: "history",
                label: "Casino Hold'Em Hand History",
                rolesAllowed: [Role.ADMIN, Role.OPERATOR],
                permissions: [PermissionCategory.HAND_HISTORY]
            },
            {
                path: '/tournament-history',
                icon: "history",
                label: "Tournament History",
                rolesAllowed: [Role.ADMIN, Role.OPERATOR],
                permissions: [PermissionCategory.HAND_HISTORY]
            }

        ]
    }

    getMenuRoutes(role: string, permissions: { [key: string]: GroupPermission }): MenuRoute[] {
        var filteredRoutes = this.getRoutes(role, permissions);
        filteredRoutes.forEach((route: any) => {
            if (route.children) {
                var children: Array<any> = [];
                route.children.forEach((c: any) => {
                    let requiredPermissions = route.permissions;
                    let requiresWriteAccess = route.writeAccess;
                    if (typeof(c.permissions) != "undefined") {
                        requiredPermissions = c.permissions;
                    }
                    if (typeof(c.writeAccess) != "undefined") {
                        requiresWriteAccess = c.writeAccess;
                    } else if (typeof(route.writeAccess) == "undefined") {
                        requiresWriteAccess = false;
                    }
                    if (this.contains(c.rolesAllowed, role) && this.hasPermission(requiredPermissions, requiresWriteAccess, permissions)) {
                        children.push(c);
                    }
                });
                route.children = children;
            }

        });

        return filteredRoutes;
    }

    isDisplayInMenu(route: any): boolean {
        return typeof(route.displayInMenu) == "undefined" || route.displayInMenu === true
    }

    getRoutes(role: string, permissions: { [key: string]: GroupPermission }): MenuRoute[] {
        var filteredRoutes = [];
        this.routes.forEach(route => {
            var allowed = this.contains(route.rolesAllowed, role);
            var permitted = this.hasPermission(route.permissions, route.writeAccess, permissions);
            if (allowed && permitted) {
                filteredRoutes.push(Object.assign({}, route));
            }
        });
        return filteredRoutes;
    }

    hasPermission(categories: string[], requiresWriteAccess: boolean, permissions: { [key: string]: GroupPermission }): boolean {
        if (!categories || categories.length == 0) {
            return true;
        }
        if (!permissions) {
            return false;
        }
        if (typeof (requiresWriteAccess) == "undefined") {
            requiresWriteAccess = false;
        }

        return categories.some(cat => {
            var permission = permissions[cat];
            if (permission) {
                if (permission.writeAccess == true && requiresWriteAccess == true) {
                    return true;
                } else if (requiresWriteAccess == false) {
                    return true;
                }
            }
            return false;
        });
    }

    contains(rolesAllowed: Array<string>, role: string): boolean {
        if (!rolesAllowed || rolesAllowed.length == 0) {
            return true;
        }
        var result = rolesAllowed.some(r => {
            return r == role;
        });
        return result;
    }
}



// WEBPACK FOOTER //
// ./src/app/service/MenuService.ts